/**
 * CSV export, moved to the client because the server cannot do it any more.
 *
 * `/api/export` in apps/display/server.py joined names onto rows and wrote the
 * file server-side. This server has no names: every reason, every person's
 * name and every correction note is ciphertext it holds no key for. So the
 * export is built here, after decryption, and never exists on any disk but the
 * one the parent saved it to.
 *
 * FORMULA INJECTION. The escaping below is ported from server.py and it is the
 * whole reason this file has tests. A reason a parent typed goes into a cell;
 * a cell beginning `=`, `+`, `-`, `@`, tab or carriage return is executed as a
 * formula by Excel, Google Sheets and LibreOffice when the file is opened.
 * `=HYPERLINK(...)` and `=cmd|...` are the usual demonstrations.
 *
 * What makes this worth a test rather than a comment: the escaping moved from
 * a server that had tests to a client that did not, and NO test on the API
 * side can ever catch its absence -- the API never sees a cell. If this
 * function quietly stopped escaping, every existing test would still pass.
 */

import { asTyped, currentUnit } from './money';
import type { Entry, Person } from './state';

/** Cells that hold text somebody typed, and therefore need escaping. */
const DANGEROUS = ['=', '+', '-', '@', '\t', '\r'];

/**
 * Neutralise a cell that a spreadsheet would run.
 *
 * A leading apostrophe is what server.py used: spreadsheets treat it as "this
 * is text" and do not display it.
 *
 * Checked against BOTH the raw value and the trimmed one, which is a small
 * deliberate divergence from the reference. server.py tested
 * `cell.lstrip().startswith(('=', '+', '-', '@', '\t', '\r'))`, and `lstrip()`
 * strips tabs and carriage returns -- so those last two entries could never
 * match anything and were dead. Nothing dangerous got through, because a tab
 * followed by `=` still trims to `=`, but a list that quietly contains two
 * impossible cases invites somebody to trust it. Testing the raw value as well
 * makes every entry in it mean what it says.
 */
export function escapeCell(value: string): string {
  const risky = (text: string) => DANGEROUS.some((lead) => text.startsWith(lead));
  return risky(value) || risky(value.trimStart()) ? `'${value}` : value;
}

/** One CSV field: escaped for spreadsheets, then quoted for CSV itself. */
function field(value: string): string {
  const safe = escapeCell(value);
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/**
 * Cents as a bare decimal, for a spreadsheet column.
 *
 * Dividing by 100 to place a decimal point is formatting, not money
 * arithmetic: no split, no rounding of a share, nothing that could disagree
 * with the ledger. The same expression is in server.py's export.
 */
function amount(cents: number): string {
  // "12.50", or "12" in a whole-unit family: see `asTyped`.
  return asTyped(cents);
}

/**
 * The amount column's heading. "Dollars" for dollars, unchanged, so a year of
 * old exports still lines up; otherwise what the family counts in.
 */
function amountHeading(): string {
  const unit = currentUnit();
  if (unit.type === 'currency') return unit.currency === 'USD' ? 'Dollars' : unit.currency;
  if (unit.type === 'marbles') return 'Marbles';
  return unit.many.charAt(0).toUpperCase() + unit.many.slice(1);
}

export interface ExportRow {
  entry: Entry;
  /** Per-person shares in cents, already decrypted and named. */
  shares: { name: string; share: number }[];
}

/**
 * The file, as text.
 *
 * Column order is server.py's, unchanged, so a family with a year of exports
 * does not suddenly get a file their spreadsheet no longer recognises.
 */
export function buildCsv(rows: ExportRow[]): string {
  const lines = [
    ['Date', amountHeading(), 'Reason', 'Type', 'Who', 'Split', 'Removed', 'Correction reason']
      .map(field)
      .join(','),
  ];

  for (const { entry, shares } of rows) {
    const who = shares.map((s) => s.name).join(', ') || 'Parents';
    const split = shares.map((s) => `${s.name} ${amount(s.share)}`).join(', ');
    lines.push(
      [
        entry.date,
        amount(entry.amount),
        entry.reason,
        entry.kind,
        who,
        split,
        entry.voided ? '1' : '0',
        entry.voidReason ?? '',
      ]
        .map(field)
        .join(','),
    );
  }

  return lines.join('\r\n') + '\r\n';
}

/**
 * Hand the file to the browser.
 *
 * The BOM is server.py's too, and it is not decoration: without it Excel on
 * Windows reads UTF-8 as the local code page and a name with an accent in it
 * arrives mangled.
 *
 * Nothing is uploaded. The blob is built in memory and released immediately,
 * so the one artefact in this system that holds names in the clear exists only
 * where the parent chose to save it.
 */
export function downloadCsv(text: string, filename: string): void {
  const blob = new Blob(['﻿', text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** Join decrypted names onto entries. The server never does this. */
export function rowsFor(entries: Entry[], people: Person[]): ExportRow[] {
  const names = new Map(people.map((person) => [person.id, person.name || 'Removed person']));
  return entries.map((entry) => ({
    entry,
    shares: Object.entries(entry.shares ?? {}).map(([id, share]) => ({
      name: names.get(id) ?? 'Removed person',
      share,
    })),
  }));
}
